/**
 * `LonaSdk.EphemeralSheet.parse`: an ephemeral sheet spec read at its JSON
 * boundary — a spec that crossed JSON (an agent's `showEphemeralSheet`
 * command, a ux spec's dispatched command) rather than one built in code.
 *
 * The spec's ids are typed here, once: the sheet's `id` as a
 * `SheetNetworkKey` (verbatim — a UUID or an `ss_<hex>` id keeps its
 * spelling), each row's `id` and `children` as `RowId`s. A field a JSON spec
 * leaves out reads as `null` (its stated none); a field of the wrong type, a
 * missing `id` / `type` / `timeScale`, or an empty id is a thrown
 * `WireDecodeError` naming `EphemeralSheet` and the path.
 */

import {
  CellId,
  Json,
  type JsonObject,
  type Option,
  RowId,
  SheetNetworkKey,
  type SheetUuid,
  WireDecodeError,
} from "@tento-lona/sheets";
import { Model } from "@tento-core/model";
import { DateTime, NaiveDate } from "@tento-chrono";
import { Rational } from "@tento-core/rational";
import { UserId } from "@tento-lona/sheets";
import { Network } from "../network.ts";
import { PartialDateMemo } from "@tento-lona/sheets/internal-sdk";

/**
 * The shapes of an ephemeral sheet spec (`LonaSdk.Client.openEphemeral`): the
 * sheet, its rows, their identity fields and their seed cells. Published on
 * `@tento/lona`; `LonaSdk.EphemeralSheet`, `LonaSdk.EphemeralRow`,
 * `LonaSdk.SeedCell`, `LonaSdk.RowIdentityFields` and `LonaSdk.SeedCellData`
 * are the same types. Declared here, beside the spec's JSON boundary, so the
 * modules that read a spec do not import the client.
 */
export namespace EphemeralSheets {
  /** Row identity fields — shared by ephemeral rows and wire format. */
  export type RowIdentityFields = {
    id: RowId;
    /** `null` for a row without a lookup key. */
    lookupKey: Option<string>;
    timeScale: Network.TimeScaleCustom;
  };

  /**
   * One seeded cell: `[date, value]`, or `[date, value, cellId]` for a cell
   * that has an id — every cell of a `multi`-cardinality row does, since its
   * id is part of its address (a date holds several).
   */
  export type SeedCell = [date: string, value: Json] | [date: string, value: Json, cellId: CellId];

  /** Pre-seeded cell data for testing/ephemeral sheets. */
  export type SeedCellData = {
    /** Cells seeded into the in-memory backend; `null` for a row with no
     *  seed. */
    data: Option<SeedCell[]>;
  };

  /**
   * Ephemeral row = creation attrs + identity + seed data. Every field is
   * stated; `null` says "none".
   */
  export type EphemeralRow = Network.NewRowWithAttributes &
    RowIdentityFields &
    SeedCellData & {
      /** Child row IDs — establishes parent-child nesting in the sheet
       *  tree; `null` for a leaf. */
      children: Option<RowId[]>;
    };

  /**
   * Ephemeral sheet input. Every field is stated; `null` says "none"
   * (`creatorId: null` seeds the sheet under the client's principal,
   * `role: null` opens it as its owner). `id` is the sheet's key as the spec
   * spells it (a UUID, or an `ss_<hex>` id such as `SheetId.ephemeral(name)`
   * spells), kept verbatim. A spec that crossed JSON is read with
   * `LonaSdk.EphemeralSheet.parse`, which types its ids.
   */
  export type EphemeralSheet = {
    id: SheetNetworkKey;
    lookupKey: Option<string>;
    title: Option<string>;
    rows: EphemeralRow[];
    creatorId: Option<UserId>;
    role: Option<Network.AccessRole>;
    description: Option<string>;
    timeScale: Option<Network.TimeScale>;
    width: Option<number>;
  };
}

const TYPE = "EphemeralSheet";
const ACCESS_ROLES: readonly Network.AccessRole[] = ["owner", "viewer", "editor"];
const TIME_SCALES: readonly Network.TimeScale[] = ["year", "month", "week", "day"];
const ROW_TIME_SCALES: readonly Network.TimeScaleCustom[] = ["year", "month", "week", "day", "custom"];

/** `raw` as an `EphemeralSheet`, or a thrown `WireDecodeError`. */
export function parseEphemeralSheet(raw: Json): EphemeralSheets.EphemeralSheet {
  const obj = objectAt(raw, []);
  const rowsRaw = obj["rows"];
  if (!Array.isArray(rowsRaw)) throw malformed(["rows"], "expected an array");
  const creator = optionalString(obj, "creatorId", []);
  const width = optionalNumber(obj, "width", []);
  return {
    id: SheetNetworkKey.fromWire(nonEmptyString(obj, "id", [])),
    lookupKey: optionalString(obj, "lookupKey", []),
    title: optionalString(obj, "title", []),
    rows: rowsRaw.map((row: Json, i: number) => parseRow(row, ["rows", String(i)])),
    creatorId: creator == null ? null : userIdAt(creator, ["creatorId"]),
    role: optionalMember(obj, "role", ACCESS_ROLES, []),
    description: optionalString(obj, "description", []),
    timeScale: optionalMember(obj, "timeScale", TIME_SCALES, []),
    width,
  };
}

function parseRow(raw: Json, path: readonly string[]): EphemeralSheets.EphemeralRow {
  const obj = objectAt(raw, path);
  const timeScale = optionalMember(obj, "timeScale", ROW_TIME_SCALES, path);
  if (timeScale == null) throw malformed([...path, "timeScale"], "missing required field");
  const childrenRaw = obj["children"];
  let children: Option<RowId[]> = null;
  if (childrenRaw != null) {
    if (!Array.isArray(childrenRaw)) throw malformed([...path, "children"], "expected an array");
    children = childrenRaw.map((c: Json, i: number) => rowIdAt(c, [...path, "children", String(i)]));
  }
  const attributes = obj["attributes"];
  return {
    id: rowIdAt(obj["id"] ?? null, [...path, "id"]),
    type: nonEmptyString(obj, "type", path),
    timeScale,
    lookupKey: optionalString(obj, "lookupKey", path),
    label: optionalString(obj, "label", path),
    formula: optionalString(obj, "formula", path),
    icon: optionalString(obj, "icon", path),
    clientId: optionalString(obj, "clientId", path),
    heightPx: optionalNumber(obj, "heightPx", path),
    attributes: attributes === undefined ? null : attributes,
    position: positionAt(obj["position"] ?? null, [...path, "position"]),
    children,
    data: dataAt(obj["data"] ?? null, [...path, "data"]),
  };
}

/**
 * Refuse a code-built spec whose seeded cell dates are not partial dates,
 * before anything is seeded: a thrown `WireDecodeError` naming
 * `EphemeralSheet`, the entry's path and the row (`EphemeralSheet.parse`
 * checks the same at the JSON boundary).
 */
export function checkSeedDates(sheet: EphemeralSheets.EphemeralSheet): void {
  const issues: Model.Issue[] = [];
  const dates = new PartialDateMemo(PartialDateMemo.CAPACITY);
  sheet.rows.forEach((row, i) => {
    row.data?.forEach(([date], j) => {
      if (dates.parseOptional(date) != null) return;
      issues.push(Model.issue(
        ["rows", String(i), "data", String(j), "0"],
        "type",
        `row ${row.id}: expected a partial date ("ymd-2026-4-23"), got "${date}"`,
      ));
    });
  });
  if (issues.length > 0) throw new WireDecodeError(TYPE, issues);
}


/**
 * The wire sheet an ephemeral `spec` opens as (`LonaSdk.Client.openEphemeral`):
 * every row owned by `principal` (as is a sheet with no stated creator), at
 * its stated position or its index, carrying its seed cells (`data`) as
 * stored cells; the sheet created and last updated now, under `id` (the
 * spec's id as the wire spells it).
 */
export function ephemeralWireSheet(
  spec: EphemeralSheets.EphemeralSheet,
  principal: UserId,
  id: SheetUuid,
): Network.UserSheetWithRows {
  const { rows: ephemeralRows, ...sheetData } = spec;
  // The seeds' dates repeat across rows: each distinct one is parsed once.
  const dates = new PartialDateMemo(PartialDateMemo.CAPACITY);

  // Convert EphemeralRow[] → Network.SheetRow[]
  const networkRows: (Network.SheetRow & {
    cells?: Option<Network.SheetCell[]>;
  })[] = ephemeralRows.map(
    (row, i) => ({
      ...Network.SheetRow.make({
        id: row.id,
        type: row.type,
        // `of` reads an omitted field as `null`.
        label: row.label ?? null,
        lookupKey: row.lookupKey,
        ownerId: principal,
        timeScale: row.timeScale,
        position: row.position ?? Rational.nth(i),
        heightPx: row.heightPx ?? null,
        formula: row.formula ?? null,
        icon: row.icon ?? null,
        attributes: row.attributes ?? null,
        children: row.children,
        metadata: null,
      }),
      cells: row.data?.map(([date, value, cellId]) =>
        Network.SheetCellSchema.make({
          date: dates.semanticTime(date),
          data: value,
          endDate: null,
          // A `multi`-cardinality row's cell is addressed by its id; the
          // hydrate refuses one without it.
          id: cellId ?? null,
        })
      ) ?? null,
    }),
  );

  const now = DateTime.utc.now();
  return Network.UserSheetWithRows.make({
    id,
    // No creator (`null`) is the client's principal.
    creatorId: sheetData.creatorId ?? principal,
    createdAt: now,
    lastUpdatedAt: now,
    role: sheetData.role ?? "owner",
    title: sheetData.title,
    description: sheetData.description,
    timeScale: sheetData.timeScale,
    width: sheetData.width,
    lookupKey: sheetData.lookupKey,
    rows: networkRows,
    metadata: null,
    position: null,
  });
}

function malformed(path: readonly string[], message: string): WireDecodeError {
  return new WireDecodeError(TYPE, [Model.issue([...path], "type", message)]);
}

function objectAt(raw: Json, path: readonly string[]): JsonObject {
  const obj = Json.object(raw);
  if (obj == null) throw malformed(path, "expected an object");
  return obj;
}

function nonEmptyString(obj: JsonObject, key: string, path: readonly string[]): string {
  const value = obj[key];
  if (typeof value !== "string" || value.length === 0) throw malformed([...path, key], "expected a non-empty string");
  return value;
}

function optionalString(obj: JsonObject, key: string, path: readonly string[]): Option<string> {
  const value = obj[key];
  if (value == null) return null;
  if (typeof value !== "string") throw malformed([...path, key], "expected a string");
  return value;
}

function optionalNumber(obj: JsonObject, key: string, path: readonly string[]): Option<number> {
  const value = obj[key];
  if (value == null) return null;
  if (typeof value !== "number") throw malformed([...path, key], "expected a number");
  return value;
}

function optionalMember<T extends string>(
  obj: JsonObject,
  key: string,
  members: readonly T[],
  path: readonly string[],
): Option<T> {
  const value = obj[key];
  if (value == null) return null;
  const member = members.find((m) => m === value);
  if (member === undefined) throw malformed([...path, key], `expected one of ${members.join(", ")}`);
  return member;
}

function rowIdAt(raw: Json, path: readonly string[]): RowId {
  if (typeof raw !== "string" || raw.length === 0) throw malformed(path, "expected a non-empty row id");
  return RowId.of(raw);
}

function userIdAt(raw: string, path: readonly string[]): UserId {
  const id = UserId.parse(raw);
  if (id == null) throw malformed(path, "expected a non-empty user id");
  return id;
}

function positionAt(raw: Json, path: readonly string[]): Option<Rational> {
  if (raw == null) return null;
  const obj = objectAt(raw, path);
  const n = obj["n"];
  const d = obj["d"];
  if (typeof n !== "number" || typeof d !== "number") throw malformed(path, "expected { n, d }");
  return Rational.make({ n, d });
}

function dataAt(raw: Json, path: readonly string[]): Option<EphemeralSheets.SeedCell[]> {
  if (raw == null) return null;
  if (!Array.isArray(raw)) throw malformed(path, "expected an array");
  return raw.map((entry: Json, i: number): EphemeralSheets.SeedCell => {
    if (!Array.isArray(entry) || (entry.length !== 2 && entry.length !== 3)) {
      throw malformed([...path, String(i)], "expected a [date, value] or [date, value, cellId] entry");
    }
    const [date, value, cellId] = entry;
    if (typeof date !== "string") throw malformed([...path, String(i), "0"], "expected a date string");
    if (NaiveDate.Partial.parseOptional(date) == null) {
      throw malformed([...path, String(i), "0"], `expected a partial date ("ymd-2026-4-23"), got "${date}"`);
    }
    if (entry.length === 2) return [date, value ?? null];
    const id = typeof cellId === "string" ? CellId.parse(cellId) : null;
    if (id == null) throw malformed([...path, String(i), "2"], "expected a non-empty cell id");
    return [date, value ?? null, id];
  });
}

/**
 * The `Network.UserSheet.id` an ephemeral handle's entry carries: the spec's
 * id, verbatim. `Network.UserSheet.id` is typed `SheetUuid`, but a spec's id may
 * be an `ss_<hex>` id (`SheetId.ephemeral(name)`), and the handle keeps that
 * spelling until the ephemeral id's runtime spelling changes (a Known gap):
 * the one place the SDK widens a spec id to the wire type.
 */
export function specNetworkId(id: SheetNetworkKey): SheetUuid {
  const spelling: string = id;
  return spelling as SheetUuid;
}
